<template>
    <div class="email-preview-widget">
        <div class="email-preview-header">
            <div class="email-preview-header-left">
                <div class="email-info" v-if="email_item">
                    <h3 class="email-subject">{{ email_item.subject || 'No Subject' }}</h3>
                    <p class="email-meta">
                        <strong>From:</strong> {{ email_item.from || email_item.created_by }}
                    </p>
                    <p class="email-meta" v-if="email_item.to">
                        <strong>To:</strong> {{ email_item.to }}
                    </p>
                    <p class="email-meta">
                        <strong>Date:</strong> {{ formatEmailDate(email_item.created_at || email_item.date) }}
                    </p>
                </div>
            </div>
            <div class="email-preview-options" v-if="email_item">

            </div>
        </div>

        <div class="email-preview-body">
            <iframe ref="emailBodyFrame" style="width: 100%; height: 100%; border: none;">
            </iframe>
        </div>

        <div class="email-preview-attachments"
            v-if="email_item && email_item.attachments && email_item.attachments.length > 0">
            <h4>Attachments:</h4>
            <ul>
                <li v-for="(attachment, index) in email_item.attachments" :key="index">
                    {{ attachment.filename }} ({{ formatFileSize(attachment.size) }})
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
export default {
    name: "EmailPreviewWidget",
    props: {
        email_item: {
            type: Object,
            default: null,
        },
    },
    watch: {
        email_item(newItem) {
            this.$nextTick(() => {
                const iframe = this.$refs.emailBodyFrame;
                if (iframe) {
                    if (newItem && (newItem.message || newItem.content)) {
                        iframe.onload = () => {
                            // Optional: Add any iframe content manipulation here
                        };

                        // Use message if available, otherwise fall back to content or a default message
                        const emailContent = newItem.message || newItem.content || newItem.action || 'No content available';
                        iframe.srcdoc = this.formatEmailContent(emailContent);
                    } else {
                        iframe.srcdoc = '<html><body style="background-color: #fafafa; padding: 20px; font-family: Arial, sans-serif;"><p>No email content available</p></body></html>';
                    }
                }
            });
        }
    },
    methods: {
        formatEmailContent(content) {
            // Check if content looks like HTML
            if (content && (content.includes('<') || content.includes('&'))) {
                return `
          <html>
            <head>
              <style>
                body {
                  font-family: 'Nunito', Arial, sans-serif;
                  font-size: 14px;
                  line-height: 1.6;
                  color: #333;
                  margin: 20px;
                  background-color: #fafafa;
                }
                p { margin: 0 0 10px 0; }
                ul, ol { margin: 0 0 10px 20px; padding-left: 20px; }
                li { margin-bottom: 5px; }
                a { color: #1a73e8; text-decoration: underline; }
                a:hover { color: #135abc; }
                h1, h2, h3, h4, h5, h6 {
                  font-family: 'Nunito', Arial, sans-serif;
                  color: #1c1c1c;
                  margin-top: 20px;
                  margin-bottom: 10px;
                  font-weight: 600;
                }
                blockquote {
                  border-left: 4px solid #ccc;
                  margin: 10px 0;
                  padding-left: 15px;
                  color: #555;
                  font-style: italic;
                }
                pre {
                  background-color: #f5f5f5;
                  border: 1px solid #ddd;
                  border-radius: 4px;
                  padding: 10px;
                  overflow-x: auto;
                  font-family: monospace;
                  font-size: 13px;
                  white-space: pre-wrap;
                  word-wrap: break-word;
                }
                code {
                  font-family: monospace;
                  background-color: #f0f0f0;
                  padding: 2px 4px;
                  border-radius: 3px;
                  font-size: 0.9em;
                }
              </style>
            </head>
            <body>${content}</body>
          </html>
        `;
            } else {
                // Plain text content - convert newlines to <br>
                const formattedContent = content
                    ? content.replace(/\r\n/g, '<br>').replace(/\n/g, '<br>')
                    : 'No content available';

                return `
          <html>
            <head>
              <style>
                body {
                  font-family: 'Nunito', Arial, sans-serif;
                  font-size: 14px;
                  line-height: 1.6;
                  color: #333;
                  margin: 20px;
                  background-color: #fafafa;
                }
              </style>
            </head>
            <body>${formattedContent}</body>
          </html>
        `;
            }
        },
        formatEmailDate(dateString) {
            if (!dateString) return 'Unknown date';

            try {
                const date = new Date(dateString);
                return date.toLocaleString();
            } catch (error) {
                return dateString;
            }
        },
        formatFileSize(size) {
            if (!size) return '0 B';

            if (size < 1024) {
                return size + ' B';
            } else if (size < 1024 * 1024) {
                return (size / 1024).toFixed(2) + ' KB';
            } else if (size < 1024 * 1024 * 1024) {
                return (size / (1024 * 1024)).toFixed(2) + ' MB';
            } else {
                return (size / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
            }
        },
        deleteEmail(item) {
            // Emit event to parent component to handle deletion
            this.$emit('deleteEmail', item);
        }
    },
};
</script>

<style lang="scss" scoped>
.email-preview-widget {
    width: 100%;
    height: 100%;
    background-color: #fff;
    border-radius: 5px;
    display: flex;
    flex-direction: column;
    padding: 15px;

    .email-preview-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        padding-bottom: 15px;
        border-bottom: 1px solid #eee;
        margin-bottom: 15px;

        .email-preview-header-left {
            display: flex;
            align-items: flex-start;
            flex: 1;

            .email-info {
                display: flex;
                flex-direction: column;

                .email-subject {
                    font-family: "Nunito", sans-serif;
                    font-size: 18px;
                    font-weight: 700;
                    color: #1c1c1c;
                    margin: 0 0 8px 0;
                }

                .email-meta {
                    font-family: "Nunito", sans-serif;
                    font-size: 12px;
                    color: #555;
                    margin: 2px 0;

                    strong {
                        font-weight: 600;
                    }
                }
            }
        }

        .email-preview-options {
            display: flex;
            align-items: center;
            margin-left: auto;
            padding-left: 15px;
        }
    }

    .email-preview-body {
        position: relative;
        background: #fafafa;
        border-radius: 10px;
        flex-grow: 1;
        display: flex;
        min-height: 400px;

        iframe {
            width: 100%;
            height: 100%;
            border: none;
            border-radius: 10px;
        }
    }

    .email-preview-attachments {
        padding: 15px;
        background: #f0f0f0;
        border-radius: 8px;
        margin-top: 15px;

        h4 {
            font-family: "Nunito", sans-serif;
            font-size: 14px;
            font-weight: 600;
            color: #333;
            margin: 0 0 10px 0;
        }

        ul {
            list-style: none;
            padding: 0;
            margin: 0;

            li {
                font-family: "Nunito", sans-serif;
                font-size: 13px;
                color: #444;
                padding: 5px 0;
                border-bottom: 1px dashed #ddd;

                &:last-child {
                    border-bottom: none;
                }
            }
        }
    }
}
</style>
